/* @jsxImportSource react */
'use client';

import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ForwardedRef,
  forwardRef,
  type Ref,
} from 'react';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import { cn } from '../../utils/cn';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

/** Valid HTML elements for the Text component's `as` prop. */
export type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'label'
  | 'dt'
  | 'dd'
  | 'li'
  | 'figcaption'
  | 'legend'
  | 'pre'
  | 'code'
  | 'em'
  | 'strong'
  | 'small'
  | 'abbr'
  | 'time';

/** Every `TextElement` except `"label"`, which carries its own optional `htmlFor`. */
type NonLabelElement = Exclude<TextElement, 'label'>;

// `color` is omitted so the deprecated `color` HTML attribute (which React
// includes on every element) can't be passed — typography color is set with a
// `text-od-content-*` class via `className`. This also turns leftover
// `<Text color="…">` usages into type errors during migration.
type BaseTextProps = Omit<StripUndesiredProps<ComponentPropsWithoutRef<'span'>>, 'color'> &
  AnalyticsProps & {
    /**
     * Tailwind classes. Set typography and color here, e.g.
     * `text-display-lg text-od-content-primary`, `text-body-sm text-od-content-tertiary`.
     * See `theme-atoms.css` for the `text-{display,header,subheader,body,label}-{size}`
     * ramp and the `od-content-*` color tokens.
     */
    className?: string;
    /** Text content. */
    children?: React.ReactNode;
  };

/**
 * Discriminated union on `as`: `htmlFor` is only accepted when rendering as
 * `<label>` (where it's optional). Every other element forbids it so a stray
 * `htmlFor` on a `<span>` is a type error. `as` defaults to `<p>`.
 */
export type TextProps = BaseTextProps &
  ({ as: 'label'; htmlFor?: string } | { as?: NonLabelElement; htmlFor?: never });

/**
 * Typography component. Both typography and color are applied with Tailwind
 * utility classes via `className` — there are no `variant`, `size`, or `color`
 * props. Pick a step from the OD ramp (`text-{display,header,subheader,body,label}-{size}`)
 * and a semantic color token (`text-od-content-*`).
 *
 * Use `as` to choose the rendered element (defaults to `<p>`); `as="label"`
 * additionally accepts `htmlFor`. For truncation, add the `truncate` class
 * (plus `min-w-0` inside flex/grid) via `className`.
 *
 * @example
 * ```tsx
 * <Text as="h1" className="text-header-lg">Page title</Text>
 * <Text as="h2" className="text-subheader-md">Section</Text>
 * <Text className="text-body-md">Default body copy</Text>
 * <Text className="text-body-sm text-od-content-tertiary">Helper text</Text>
 * <Text as="label" htmlFor="email" className="text-label-md">Form label</Text>
 * <Text className="text-body-md truncate min-w-0">Clipped…</Text>
 * ```
 */
function TextInner(
  { children, className, as, analyticsName, trackImpression, ...props }: TextProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const Component = as ?? 'p';

  const viewRef = useImpressionRef(
    Boolean(analyticsName && trackImpression),
    analyticsName,
    'textViewEvent',
    ref as Ref<HTMLElement>,
  );

  return (
    <Component
      // The dynamic `Component` tag creates an impossible intersection of ref
      // types across all TextElement members. We widen to the common base
      // (HTMLElement) which is safe — all text elements extend HTMLElement.
      ref={viewRef}
      // `m-0` resets the user-agent margins block variants (h1–h6, p) carry by
      // default — atoms is margin-free (spacing comes from gap/padding), so Text
      // should never introduce its own. Consumer `className` still wins via cn.
      className={cn('m-0', className)}
      {...props}
    >
      {children}
    </Component>
  );
}

/** Public call signature for the polymorphic `Text` component (props plus an optional `ref`). */
type TextComponent = (
  props: TextProps & { ref?: ForwardedRef<ElementRef<'span'>> },
) => React.ReactElement;

/**
 * Typography primitive for all text in the OD design language. Typography and
 * color are applied with Tailwind utility classes via `className` — pick a step
 * from the OD ramp (`text-{display,header,subheader,body,label}-{size}`) and a
 * semantic color token (`text-od-content-*`).
 *
 * Use `as` to choose the rendered element (defaults to `<p>`); `as="label"`
 * additionally accepts `htmlFor`. Set `analyticsName` (plus `trackImpression`)
 * to emit a view event the first time it scrolls into view.
 *
 * @example
 * ```tsx
 * <Text as="h1" className="text-header-lg">Page title</Text>
 * <Text className="text-body-md">Default body copy</Text>
 * <Text className="text-body-sm text-od-content-tertiary">Helper text</Text>
 * <Text as="label" htmlFor="email" className="text-label-md">Email</Text>
 * ```
 */
// eslint-disable-next-line typescript/no-unsafe-type-assertion -- polymorphic component: re-typing forwardRef's result to the public TextProps signature is the standard pattern
export const Text = forwardRef(TextInner) as TextComponent;
